import { Alg } from "cubing/alg";
import { ICubingService, StackmatState } from "./ICubingService";
import { createStartAnimationLoop } from './timerUtils';

export interface SampleData {
    raw: number[];
    bin: number[];
    bits: number[];
    bytes: string[];
}

type StackmatCallback = (state: StackmatState) => void;

class StackmatService implements ICubingService{
    private audioContext: AudioContext | null = null;
    private audioStream: MediaStream | null = null;
    private source: MediaStreamAudioSourceNode | null = null;
    private node: ScriptProcessorNode | null = null;
    private callback: StackmatCallback | null = null;
    private isInitialized = false;


    private lastPower = 1;
    private agcFactor = 0.0001;

    private animationStartOffset: number = 0;
    private cancelTickLoop: (() => void) | null = null;

    private state: StackmatState = {
        timeMilli: 0,
        unit: 10,
        on: false,
        greenLight: false,
        leftHand: false,
        rightHand: false,
        running: false,
        unknownRunning: true,
        signalHeader: 'I',
        noise: 1,
        power: 1,
        deviceId: "unknown"
    };

    private sampleRate: number | undefined;
    private curTimer: string | undefined;
    private bitAnalyzer: ((bit: number) => void) | undefined;

    async validateFacelets(alg: Alg): Promise<boolean> {
        return true;
    }

    setCallback(cb: StackmatCallback) {
        this.callback = cb;
    }

    getCurrentState() {
        return this.state;
    }

    setReady(alg: Alg){

    };

    async init(timer: string): Promise<void> {
        const deviceId = localStorage.getItem('selectedMicrophoneDeviceId');
        if (this.isInitialized) return;

        this.curTimer = timer;

        if (!navigator.mediaDevices?.getUserMedia) {
            throw new Error("getUserMedia not available");
        }


        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: false,
                noiseSuppression: false,
                ...(deviceId && { deviceId: { exact: deviceId } }),
            }
        });

        this.sampleRate = this.curTimer === "m"
            ? this.audioContext.sampleRate / 8000
            : this.audioContext.sampleRate / 1200;

        this.bitAnalyzer = this.curTimer === "m" ? this.appendBitMoyu : this.appendBit;

        this.agcFactor = 0.001 / this.sampleRate;
        this.lastVal.length = Math.ceil(this.sampleRate / 6);
        this.bitBuffer = [];
        this.byteBuffer = [];

        const source = this.audioContext.createMediaStreamSource(stream);
        const processor = this.audioContext.createScriptProcessor(1024, 1, 1);

        processor.onaudioprocess = (e: AudioProcessingEvent) => {
            const input = e.inputBuffer.getChannelData(0);
            // You would decode raw input to update state here
            for (let i = 0; i < input.length; i++) {
                const power = input[i] * input[i];
                this.lastPower = Math.max(0.0001, this.lastPower + (power - this.lastPower) * this.agcFactor);
                const gain = 1 / Math.sqrt(this.lastPower);
                this.procSignal(input[i] * gain);
            }
        };

        source.connect(processor);
        processor.connect(this.audioContext.destination);
        this.stackmatState = {
            ...this.stackmatState,
            deviceId: deviceId ?? "unknown"
        }
        this.audioStream = stream;
        this.source = source;
        this.node = processor;
        this.isInitialized = true;
    }

    stop() {
        this.cancelTickLoop?.();
        this.cancelTickLoop = null;

        if (this.audioStream) {
            this.audioStream.getTracks().forEach((t) => t.stop());
        }
        this.node?.disconnect();
        this.source?.disconnect();
        this.node = null;
        this.source = null;
        this.audioStream = null;

        if (this.audioContext && this.audioContext.state !== "closed") {
            this.audioContext.close();
        }

        this.audioContext = null;
        this.isInitialized = false;
    }

    //========== Audio2Bits Part ==========
    private lastVal: number[] = [];
    private lastSgn = 0;
    private THRESHOLD_SCHM = 0.2;
    private THRESHOLD_EDGE = 0.7;
    private lenVoltageKeep = 0;
    private distortionStat = 0;

    procSignal(signal: number): void {
        this.lastVal.unshift(signal);

        const isEdge =
            (this.lastVal.pop()! - signal) * (this.lastSgn ? 1 : -1) > this.THRESHOLD_EDGE &&
            Math.abs(signal - (this.lastSgn ? 1 : -1)) - 1 > this.THRESHOLD_SCHM &&
            this.lenVoltageKeep > this.sampleRate! * 0.6;

        if (isEdge) {
            for (let i = 0; i < Math.round(this.lenVoltageKeep / this.sampleRate!); i++) {
                this.bitAnalyzer?.(this.lastSgn);
            }
            this.lastSgn ^= 1;
            this.lenVoltageKeep = 0;
        } else if (this.lenVoltageKeep > this.sampleRate! * 2) {
            this.bitAnalyzer?.(this.lastSgn);
            this.lenVoltageKeep -= this.sampleRate!;
        }
        this.lenVoltageKeep++;

        if (this.lastBitLength < 10) {
            this.distortionStat = Math.max(0.0001, this.distortionStat + (Math.pow(signal - (this.lastSgn ? 1 : -1), 2) - this.distortionStat) * this.agcFactor);
        } else if (this.lastBitLength > 100) {
            this.distortionStat = 1;
        }

        if (this.sampleRemain > 0) {
            this.sampleRemain--;
            this.sampleData.raw.push(signal);
            this.sampleData.bin.push(this.lastSgn);
            if (this.sampleRemain === 0) {
                this.sampleCallback?.(this.sampleData);
                this.sampleCallback = null;
            }
        }
    }

    //========== Bits Analyzer ==========
    private bitBuffer: number[] = [];
    private byteBuffer: string[] = [];
    private idleVal = 0;
    private lastBit = 0;
    private lastBitLength = 0;
    private noStateLength = 0;


    private stackmatState: StackmatState = {
        timeMilli: 0,
        unit: 10,
        on: false,
        greenLight: false,
        leftHand: false,
        rightHand: false,
        running: false,
        unknownRunning: true,
        signalHeader: 'I',
        noise: 1,
        power: 1,
        deviceId: "unknown"
    };

    appendBit(bit: number): void {
        let newByte: string | null = null;
        this.bitBuffer.push(bit);

        if (bit != this.lastBit) {
            this.lastBit = bit;
            this.lastBitLength = 1;
        } else {
            this.lastBitLength++;
        }
        this.noStateLength++;

        if (this.lastBitLength > 10) {
            this.idleVal = bit;
            //bitBuffer.length = 0;
            this.bitBuffer = [];

            if (this.byteBuffer.length !== 0) {
                //byteBuffer.length = 0;
                this.byteBuffer = [];
            }

            if (this.lastBitLength > 100 && this.stackmatState.on) {
                this.stackmatState.on = false;
                this.stackmatState.noise = Math.min(1, this.distortionStat) || 0;
                this.stackmatState.power = this.lastPower;
                this.callback?.(this.stackmatState);
            } else if (this.noStateLength > 700) {
                this.noStateLength = 100;
                this.stackmatState.noise = Math.min(1, this.distortionStat) || 0;
                this.stackmatState.power = this.lastPower;
                this.callback?.(this.stackmatState);
            }
        } else if (this.bitBuffer.length === 10) {
            if (this.bitBuffer[0] == this.idleVal || this.bitBuffer[9] != this.idleVal) {
                this.bitBuffer = this.bitBuffer.slice(1);
            } else {
                let val = 0;
                for (let i = 8; i > 0; i--) {
                    val = (val << 1) | (this.bitBuffer[i] == this.idleVal ? 1 : 0);
                }
                newByte = String.fromCharCode(val);
                this.byteBuffer.push(newByte);
                this.decode(this.byteBuffer);
                //bitBuffer.length = 0;
                this.bitBuffer = [];
            }
        }

        if (this.sampleRemain > 0) {
            this.sampleData.bits.push(bit);
            if (newByte) {
                this.sampleData.bytes.push(newByte);
            }
        }
    }

    decode(byteBuffer: string[]): void {
        if (byteBuffer.length !== 9 && byteBuffer.length !== 10) {
            return;
        }
        //console.log('[stackmat]', byteBuffer);

        const reHead = /[ SILRCA]/;
        const reNumber = /[0-9]/;
        const head = byteBuffer[0];
        

        if (!reHead.exec(head)) {
            console.log(byteBuffer)
            return;
        }

        let checksum = 64;
        for (let i = 1; i < byteBuffer.length - 3; i++) {
            if (!reNumber.exec(byteBuffer[i])) {
                //console.log(`Found ${byteBuffer[i]}`)
                return;
            }
            checksum += ~~(byteBuffer[i]);
        }

        if (checksum != byteBuffer.at(-3)!.charCodeAt(0)) {
            return;
        }

        const timeMilli = ~~byteBuffer[1] * 60000 +
            ~~(byteBuffer[2] + byteBuffer[3]) * 1000 +
            ~~(byteBuffer[4] + byteBuffer[5] + (byteBuffer.length === 10 ? byteBuffer[6] : '0'));

        this.pushNewState(head, timeMilli, byteBuffer.length === 9 ? 10 : 1);
    }

    private lastSucTime = 0;

    pushNewState(head: string, timeMilli: number, unit: number): void {
        const sucTime = Date.now();

        if (sucTime - this.lastSucTime > 200) {
            //console.log('[stackmat] signal miss', sucTime - lastSucTime);
        }
        this.lastSucTime = sucTime;

        const newState: StackmatState = {
            ...this.stackmatState,
            timeMilli,
            unit,
            on: true,
            greenLight: head === 'A',
            leftHand: head === 'L' || head === 'A' || head === 'C',
            rightHand: head === 'R' || head === 'A' || head === 'C',
            running: (head !== 'S' || this.stackmatState.signalHeader === 'S') && (head === ' ' || timeMilli > this.stackmatState.timeMilli),
            unknownRunning: !this.stackmatState.on,
            signalHeader: head,
            noise: Math.min(1, this.distortionStat) || 0,
            power: this.lastPower,
        };
        const wasRunning = this.stackmatState.running;
        this.stackmatState = newState;
        this.noStateLength = 0;
        this.callback?.(this.stackmatState);

        if (!wasRunning && newState.running) {
            // Timer just started
            this.animationStartOffset = performance.now();

            this.cancelTickLoop?.(); // cancel previous loop if any
            this.cancelTickLoop = createStartAnimationLoop(
                () => this.stackmatState.running,
                () => this.stackmatState,
                (updated) => {
                    this.stackmatState = updated;
                    this.callback?.(updated);
                },
                this.animationStartOffset
            );
        }

        if (wasRunning && !newState.running) {
            this.cancelTickLoop?.();
            this.cancelTickLoop = null;
        }
    }

    appendBitMoyu(bit: number): void {
        if (this.lastBit !== this.idleVal && this.lastBitLength === 1) {
            this.bitBuffer.push(bit);

            if (this.bitBuffer.length === 24) {
                let timeMilli = 0;
                for (let i = 5; i >= 0; i--) {
                    timeMilli *= 10;
                    for (let j = 0; j < 4; j++) {
                        timeMilli += this.bitBuffer[i * 4 + j] << j;
                    }
                }
                this.bitBuffer.length = 0;
                this.pushNewState('S', timeMilli, 1);
            }
        }

        if (bit !== this.lastBit) {
            this.lastBit = bit;
            this.lastBitLength = 1;
        } else {
            this.lastBitLength++;
        }

        if (this.lastBitLength > 10) {
            this.idleVal = bit;
            this.bitBuffer.length = 0;
            this.byteBuffer.length = 0;

            if (this.lastBitLength > 1000) {
                this.stackmatState.on = false;
                this.stackmatState.noise = Math.min(1, this.distortionStat) || 0;
                this.stackmatState.power = this.lastPower;
                this.callback?.(this.stackmatState);
            }
        }

        if (this.sampleRemain > 0) {
            this.sampleData.bits.push(bit);
        }
    }

    sampleCallback: ((data: SampleData) => void) | null = null;
    private sampleRemain = 0;
    private sampleData: SampleData = {
        raw: [],
        bin: [],
        bits: [],
        bytes: [],
    };

    getSample(duration: number, callback: (data: SampleData) => void): void {
        this.sampleCallback = callback;
        this.sampleRemain = Math.ceil(duration * (this.audioContext?.sampleRate || 44100));
        this.sampleData = { raw: [], bin: [], bits: [], bytes: [] };
    }

}

export const stackmatService = new StackmatService();
